import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ArrowLeft, Trophy } from 'lucide-react';

export const RoundSummary = ({ round, onClose }) => {
  const s = round.summary || {};
  const toPar = s.score_to_par;
  const toParStr = toPar === 0 ? 'E' : toPar > 0 ? `+${toPar}` : `${toPar}`;
  const [expandedHole, setExpandedHole] = useState(null);

  const getScoreBadge = (diff) => {
    if (diff <= -2) return 'bg-yellow-500 text-white rounded-full';
    if (diff === -1) return 'border-2 border-red-500 text-red-500 rounded-full';
    if (diff === 0) return 'text-foreground';
    if (diff === 1) return 'border-2 border-blue-500 text-blue-500 rounded-md';
    return 'border-2 border-blue-600 text-blue-600 rounded-md';
  };

  return (
    <div className="space-y-4" data-testid="round-summary">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="summary-back"><ArrowLeft className="w-5 h-5" /></Button>
        <div><h2 className="text-lg font-bold text-foreground">Round Summary</h2><p className="text-sm text-muted-foreground">{round.course_name} · {round.tee_name}</p></div>
      </div>
      <div className="bg-card rounded-xl border border-border/50 p-6 text-center">
        <div className="text-5xl font-black text-foreground">{s.total_strokes}</div>
        <div className={`text-xl font-bold mt-1 ${toPar < 0 ? 'text-red-500' : toPar === 0 ? 'text-foreground' : 'text-blue-500'}`}>{toParStr}</div>
        <div className="text-sm text-muted-foreground">{s.holes_played} holes · Par {s.total_par}</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Card className="border-border/50"><CardContent className="p-3 text-center"><div className="text-xl font-bold text-foreground">{s.total_putts}</div><div className="text-xs text-muted-foreground">Total Putts</div></CardContent></Card>
        <Card className="border-border/50"><CardContent className="p-3 text-center"><div className="text-xl font-bold text-foreground">{s.putts_per_hole}</div><div className="text-xs text-muted-foreground">Putts/Hole</div></CardContent></Card>
        <Card className="border-border/50"><CardContent className="p-3 text-center"><div className="text-xl font-bold text-foreground">{s.fairway_pct}%</div><div className="text-xs text-muted-foreground">Fairways</div></CardContent></Card>
        <Card className="border-border/50"><CardContent className="p-3 text-center"><div className="text-xl font-bold text-foreground">{s.gir_pct}%</div><div className="text-xs text-muted-foreground">GIR</div></CardContent></Card>
      </div>
      <div className="bg-card rounded-xl border border-border/50 overflow-hidden">
        <div className="px-4 py-2 bg-muted/50 border-b border-border/30 flex items-center justify-between">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Scorecard</span>
          <span className="text-[10px] text-muted-foreground">Tap a hole for details</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/50 bg-muted/30">
                <th className="py-2 px-3 text-left font-bold text-muted-foreground">Hole</th>
                <th className="py-2 px-2 text-center font-bold text-muted-foreground">Par</th>
                <th className="py-2 px-2 text-center font-bold text-muted-foreground">Score</th>
                <th className="py-2 px-2 text-center font-bold text-muted-foreground">Putts</th>
                <th className="py-2 px-2 text-center font-bold text-muted-foreground">FW</th>
                <th className="py-2 px-2 text-center font-bold text-muted-foreground">Pen</th>
              </tr>
            </thead>
            <tbody>
              {(round.hole_scores || []).map((h) => {
                const diff = h.strokes - h.par;
                const isExpanded = expandedHole === h.hole_number;
                return (
                  <React.Fragment key={h.hole_number}>
                    <tr className={`border-b border-border/20 cursor-pointer transition-colors ${isExpanded ? 'bg-primary/5' : 'hover:bg-muted/30'}`}
                      onClick={() => setExpandedHole(isExpanded ? null : h.hole_number)} data-testid={`summary-hole-row-${h.hole_number}`}>
                      <td className="py-2 px-3 font-bold">{h.hole_number}</td>
                      <td className="py-2 px-2 text-center text-muted-foreground">{h.par}</td>
                      <td className="py-2 px-2 text-center"><span className={`inline-flex items-center justify-center w-7 h-7 text-xs font-bold ${getScoreBadge(diff)}`}>{h.strokes}</span></td>
                      <td className="py-2 px-2 text-center">{h.putts}</td>
                      <td className="py-2 px-2 text-center capitalize">{h.par >= 4 ? (h.drive_accuracy === 'hit' ? '\u2713' : '\u2717') : '-'}</td>
                      <td className="py-2 px-2 text-center">{h.penalties || 0}</td>
                    </tr>
                    {isExpanded && (
                      <tr data-testid={`summary-hole-detail-${h.hole_number}`}>
                        <td colSpan="6" className="p-3 bg-muted/10 border-b border-border/30">
                          <div className="grid grid-cols-4 gap-2 text-[10px]">
                            <div className="bg-background rounded-lg p-2 text-center"><div className="font-bold text-foreground capitalize">{h.par >= 4 ? (h.drive_accuracy || '-') : 'N/A'}</div><div className="text-muted-foreground">Tee Shot</div></div>
                            <div className="bg-background rounded-lg p-2 text-center"><div className="font-bold text-foreground">{h.putts}</div><div className="text-muted-foreground">Putts</div></div>
                            <div className="bg-background rounded-lg p-2 text-center"><div className="font-bold text-foreground">{h.tee_club || '-'}</div><div className="text-muted-foreground">Club</div></div>
                            <div className="bg-background rounded-lg p-2 text-center"><div className="font-bold text-foreground">{h.gir ? '\u2713' : '\u2717'}</div><div className="text-muted-foreground">GIR</div></div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <div className="bg-background rounded-lg p-2 text-[10px]">
                              <div className="font-bold text-foreground mb-0.5">Penalties ({h.penalties || 0})</div>
                              {(h.penalties || 0) > 0 ? (
                                <div className="flex gap-2 text-muted-foreground">
                                  {(h.penalty_details?.h2o || 0) > 0 && <span>H2O: {h.penalty_details.h2o}</span>}
                                  {(h.penalty_details?.ob || 0) > 0 && <span>OB: {h.penalty_details.ob}</span>}
                                  {(h.penalty_details?.dropped || 0) > 0 && <span>Drop: {h.penalty_details.dropped}</span>}
                                  {!h.penalty_details && <span>{h.penalties}</span>}
                                </div>
                              ) : <div className="text-muted-foreground">None</div>}
                            </div>
                            <div className="bg-background rounded-lg p-2 text-[10px]">
                              <div className="font-bold text-foreground mb-0.5">Bunkers ({h.bunkers || 0})</div>
                              {(h.bunkers || 0) > 0 ? (
                                <div className="flex gap-2 text-muted-foreground">
                                  {(h.bunker_details?.greenside || 0) > 0 && <span>GS: {h.bunker_details.greenside}</span>}
                                  {(h.bunker_details?.fairway || 0) > 0 && <span>FW: {h.bunker_details.fairway}</span>}
                                  {!h.bunker_details && <span>{h.bunkers}</span>}
                                </div>
                              ) : <div className="text-muted-foreground">None</div>}
                            </div>
                          </div>
                          {h.first_putt_distance && (<div className="mt-2 text-[10px] text-muted-foreground">1st Putt: <span className="font-semibold text-foreground">{h.first_putt_distance}</span></div>)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              <tr className="bg-muted/30 font-bold">
                <td className="py-2 px-3">Total</td>
                <td className="py-2 px-2 text-center">{s.total_par}</td>
                <td className="py-2 px-2 text-center">{s.total_strokes}</td>
                <td className="py-2 px-2 text-center">{s.total_putts}</td>
                <td className="py-2 px-2 text-center">{s.fairway_pct}%</td>
                <td className="py-2 px-2 text-center">{(round.hole_scores || []).reduce((sum, h) => sum + (h.penalties || 0), 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
